import { useEffect } from "react";
import { BackButton, Button } from "../../components";
import { WarrntyItemSteps } from "./types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { classNames } from "../../helper";
import { PiCheckBold } from "react-icons/pi";
import RouteNames from "../../routes/type";
import { FormProvider, useForm } from "react-hook-form";
import LineItem from "./LineItem";
import MissingLineItem from "./MissingLineItem";
import SubmitWarrntyItems from "./SubmitWarrntyItems";
import { connect, useDispatch } from "react-redux";
import Loader from "../../components/Loader";
import { RootState } from "../../store/root-reducer";
import { isScopeLoading } from "../../store/scope-loading/helpers";
import {
  addWarrntyItem,
  setWarrntyClaimId,
  uploadInvoice,
} from "../../store/add-ace2-warrnty-item/actions";
import { useTranslation } from "react-i18next";
import { Ace2WarrntyItemScope } from "../../store/add-ace2-warrnty-item/types";

function AddWarrantyItem({ loading }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formMethods = useForm({
    defaultValues: {
      file: null,
      vendor: "",
      sku: "",
      model: "",
      quantity: 1,
      price: "",
      itemType: "",
      manufacturer: "",
      material: "",
      color: "",
    },
  });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { handleSubmit } = formMethods;
  const stepId = parseInt(searchParams.get("step") || "1", 10);
  const claimId: string = searchParams.get("claimId") || "";
  const warranty_id: string = searchParams.get("warranty_id") || "";

  const currentStep = WarrntyItemSteps.find((item) => item.step_num === stepId);
  useEffect(() => {
    if ((!stepId && !/^[0-9]*$/.test(`${stepId}`)) || stepId > 3) {
      navigate(`/${RouteNames.ADD_ACE2_WARRNTY_ITEM}?step=1`, {
        replace: true,
      });
    }
    return () => {};
  }, [navigate, stepId]);

  useEffect(() => {
    dispatch(setWarrntyClaimId({ warranty_id, claim_id: claimId }));
    return () => {};
  }, [claimId, dispatch, warranty_id]);

  const handleNext = (values) => {
    switch (stepId) {
      case 1:
        const files = Array.from(values?.file || []);
        if (files.length > 0) {
          dispatch(uploadInvoice(files));
        }
        break;

      case 2:
        dispatch(
          addWarrntyItem({
            vendor: values?.vendor || "",
            sku: values?.sku || "",
            model: values?.model || "",
            quantity: values?.quantity || "",
            price: values?.price || "",
            itemType: values?.itemType || "",
            manufacturer: values?.manufacturer || "",
            material: values?.material || "",
            color: values?.color || "",
          })
        );
        break;
      default:
    }
  };

  const renderStepComponent = () => {
    switch (stepId) {
      case 1:
        return <MissingLineItem key="stepId1" />;
      case 2:
        return <LineItem key="stepId2" />;
      default:
        return null;
    }
  };

  return (
    <div className="px-4 bg-white">
      <div className="mb-8 mt-6 flex justify-between items-center">
        <BackButton />
        <div className="flex flex-row gap-2">
          {WarrntyItemSteps.map(({ step_num: step }) => {
            const isCompleted = step < stepId;
            const isDraft = step > stepId;
            return (
              <button
                disabled
                key={step}
                className={classNames(
                  "size-7 flex rounded-full justify-center items-center text-base",
                  isDraft
                    ? "bg-[#009CAA] bg-opacity-15 text-[#009CAA]"
                    : "bg-[#009CAA] text-white"
                )}
              >
                {isCompleted ? <PiCheckBold size={16} color="white" /> : step}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mb-2">
        <h3 className="text-2xl font-semibold">{t(currentStep?.title)}</h3>
      </div>
      <form className="grid gap-y-4" onSubmit={handleSubmit(handleNext)}>
        <Loader isLoading={loading} />
        <FormProvider {...formMethods}>{renderStepComponent()}</FormProvider>
        <Button submit className="my-4">
          {stepId > 1 ? t("Submit") : t("Next")}
        </Button>
        <SubmitWarrntyItems key="stepId3" />
      </form>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  loading: isScopeLoading(state.scopeLoading, Ace2WarrntyItemScope),
});

export default connect(mapStateToProps)(AddWarrantyItem);
